import { FC, ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import Logo from '../../assets/New-images/logo.svg'
import * as CONFIG from '../../config'
import { useGlobalContext } from '../../context/GlobalContext'
import {
  getSelectedAgent,
  updateSelectedAgent,
} from '../../store/agents/actions'
import {
  Channels,
  Dashboard,
  Form,
  Knowledge,
  Maximize,
  Question,
  Quiz,
  Star,
  Users,
} from '../UI/Icons'
import { CustomLink } from './CustomLink'
// import WhatsNewPopup from '@/routes/Welcome/WhatsNewPopup'
import CommonService from '@/services/CommonService'
// Import MUI components
import {
  Drawer,
  List,
  Typography,
  Divider,
  Box,
  useTheme,
  IconButton,
  Stack,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import logoHead from '@/assets/images/logo-head.svg'
import { KNOWLEDGE_ALL, USER_MANAGEMENT } from '../../config'

const commonService = new CommonService()

interface SidebarItem {
  icon: ReactNode
  label: string
  link: string
}

const windowObj: any = window

interface SidebarProps {
  // handleWhatsNewClick: () => void
  isCollapsed: boolean
  toggleCollapse: () => void
}

const Sidebar: FC<SidebarProps> = ({
  // handleWhatsNewClick,
  isCollapsed,
  toggleCollapse,
}) => {
  const { t } = useTranslation('translation')
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const { setSelectedAgentName, setSelectedAgentEmail } = useGlobalContext()
  const theme = useTheme()

  const handleAgentUpdate = async (agentId: string) => {
    try {
      const selectAgentReponse: any = await dispatch(getSelectedAgent(agentId))
      const updateAgentResponse: any = await dispatch(
        updateSelectedAgent({
          states: {
            selectedAgent: selectAgentReponse._id,
            selectedAgentName: selectAgentReponse.name,
            showToster: false,
          },
        }),
      )
      setSelectedAgentName(updateAgentResponse.selectedAgentName)
      setSelectedAgentEmail(updateAgentResponse.selectedAgentName)

      localStorage.setItem('selectedAgent', updateAgentResponse.selectedAgent)
      localStorage.setItem(
        'selectedAgentName',
        updateAgentResponse.selectedAgentName,
      )
      localStorage.setItem(
        'selectedAgentEmail',
        updateAgentResponse.selectedAgentName,
      )
      localStorage.setItem(
        'selectedLanguageCode',
        updateAgentResponse.selectedLanguageCode,
      )
    } catch (error) {
      console.error('Error updating agent:', error)
    }
  }

  useEffect(() => {
    const currentAgent = localStorage.getItem('selectedAgent')
    const searchParams = new URLSearchParams(location.search)
    const urlAgentId = searchParams.get('agent')

    if (urlAgentId && urlAgentId !== currentAgent) {
      handleAgentUpdate(urlAgentId)
    } else if (currentAgent && !searchParams.has('agent')) {
      searchParams.append('agent', currentAgent)
      history.replace({ search: searchParams.toString() })
    }
  }, [location, history])

  const categories: {
    category: string
    items: SidebarItem[]
  }[] = [
    {
      category: '#',
      items: [
        {
          icon: <Dashboard />,
          label: t('Dashboard'),
          link: '/dashboard',
        },
      ],
    },
    {
      category: t('Set Up'),
      items: [
        {
          icon: <Knowledge />,
          label: t('Knowledge'),
          link: KNOWLEDGE_ALL,
        },
        {
          icon: <Quiz />,
          label: t('Activities'),
          link: CONFIG.EXERCISES,
        },
        {
          icon: <Form />,
          label: t('Forms'),
          link: '/forms',
        },
      ],
    },
    {
      category: t('Manage'),
      items: [
        {
          icon: <Channels />,
          label: t('Channels'),
          link: '/chat-customisation?viewMode=true',
        },
        {
          icon: <Users />,
          label: t('Users'),
          link: `${USER_MANAGEMENT}?viewMode=true`,
        },
      ],
    },
  ]

  return (
    <Box
      sx={{
        width: isCollapsed ? 70 : 200,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      <IconButton
        onClick={toggleCollapse}
        sx={{
          position: 'absolute',
          // display: { xs: 'none', md: 'flex' },
          display: 'flex',
          right: -15,
          top: 20,
          backgroundColor: 'white',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          border: '1px solid #eaeaea',
          width: 30,
          height: 30,
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
          zIndex: 9999,
        }}
      >
        {isCollapsed ? (
          <ChevronRightIcon fontSize="small" />
        ) : (
          <ChevronLeftIcon fontSize="small" />
        )}
      </IconButton>

      <Box
        sx={{
          p: isCollapsed ? 1 : 2,
          display: 'flex',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
        }}
      >
        <img
          src={isCollapsed ? logoHead : Logo}
          alt="Logo"
          style={{ maxWidth: isCollapsed ? '40px' : '100%' }}
        />
      </Box>

      <Stack flexGrow={1} sx={{ overflow: 'auto', gap: 2 }}>
        {categories.map(({ category, items }, categoryIndex) =>
          category === '#' ? (
            <List key={categoryIndex} disablePadding>
              {items.map((item, index) => (
                <CustomLink key={index} {...item} isCollapsed={isCollapsed} />
              ))}
            </List>
          ) : (
            <Box key={categoryIndex}>
              {!isCollapsed && (
                <Typography
                  variant="subtitle2"
                  sx={{
                    px: 2,
                    color: '#79767A',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                >
                  {category}
                </Typography>
              )}
              <List disablePadding sx={{ mt: 0.5 }}>
                {items.map((item, index) => (
                  <CustomLink key={index} {...item} isCollapsed={isCollapsed} />
                ))}
              </List>
            </Box>
          ),
        )}
      </Stack>

      <CustomLink
        icon={<Maximize />}
        label={t('Learner View')}
        isExternal
        primaryColor
        link={`${windowObj.CHATBOT_URL}?agent=${localStorage.getItem(
          'selectedAgent',
        )}&through=admin-portal`}
        isCollapsed={isCollapsed}
      />

      <Divider sx={{ pt: 2 }} />

      <Box sx={{ py: 2 }}>
        <CustomLink
          icon={<Star />}
          label={t("What's New")}
          // onClick={() => handleWhatsNewClick()}
          link="https://noodlefactory-support.notion.site/New-Releases-247a6979da7e80a3a505e9189a3b1e6a"
          isExternal
          isCollapsed={isCollapsed}
        />
        <CustomLink
          icon={<Question />}
          label={t('Support')}
          link={CONFIG.SUPPORT}
          isCollapsed={isCollapsed}
        />
      </Box>
    </Box>
  )
}

interface CustomSidebarProps {
  // isShown: boolean
  // onToggle: () => void
  isCollapsed: boolean
  toggleCollapse: () => void
}

const CustomSidebar: FC<CustomSidebarProps> = ({
  isCollapsed,
  toggleCollapse,
}) => {
  // const [showsWhatsNew, setShowsWhatsNew] = useState<boolean>(false)
  const theme = useTheme()

  const getStates = async () => {
    const resp: any = await commonService.getUserStates()
    const { status, data } = resp
    if (status === 200) {
      if (data?.data?.states) {
        const states = data.data.states
        if (states?.showsWhatsNew === true) {
          // setShowsWhatsNew(true)
          commonService.updateUserStates({
            states: {
              showsWhatsNew: false,
            },
          })
        }
      }
    }
  }

  // const hideWhatsNewPopup = () => {
  //   setShowsWhatsNew(false)
  // }

  // const handleWhatsNewClick = () => {
  //   setShowsWhatsNew(true)
  // }

  useEffect(() => {
    getStates()
  }, [])

  return (
    <>
      {/* Desktop Drawer - persistent */}
      <Drawer
        variant="permanent"
        sx={{
          // display: { xs: 'none', md: 'block' },
          display: 'block',
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: isCollapsed ? 70 : 200,
            boxShadow: '0 0 10px rgba(0,0,0,0.1)',
            border: 'none',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflow: 'visible',
          },
        }}
        open
      >
        <Sidebar
          // handleWhatsNewClick={handleWhatsNewClick}
          isCollapsed={isCollapsed}
          toggleCollapse={() => {
            toggleCollapse()
          }}
        />
      </Drawer>

      {/* <WhatsNewPopup show={showsWhatsNew} onHide={hideWhatsNewPopup} /> */}
    </>
  )
}

export default CustomSidebar
